// @ts-check

import Splide from '@splidejs/splide';
interface SelectorOptions {
  content: string,
  thumbs: string,
  thumbTrigger: string,
  progressBar: string,
}
// see https://splidejs.com/guides/options
interface AccordionOptions {
  type: "slide" | "loop" | "fade",
  perPage: number,
  autoHeight: boolean,
  pagination: boolean,
  rewind: boolean,
  arrows: boolean,
  height: number | string,
  speed: number,
  breakpoints: {
    [bp: number]: {
      destroy: boolean,
    }
  }
}

interface ThumbnailOptions {
  rewind: boolean,
  direction: "ltr" | "rtl" | "ttb",
  pagination: boolean,
  fixedWidth: number | string,
  gap: number,
  isNavigation: boolean,
  height: number | string,
  arrows: boolean,
  drag: boolean | "free",
}

interface Options {
  selectors: SelectorOptions,
  content: AccordionOptions,
  thumbnails: ThumbnailOptions,
}

const options: Options = {
  selectors: {
    content: '.sb-accordion--content',
    thumbs: '.sb-accordion--thumbs',
    thumbTrigger: '.sb-c-accordion__content',
    progressBar: '.sb-accordion-progress-bar',
  },
  content: {
    type: 'fade',
    perPage: 1,
    autoHeight: false,
    pagination: false,
    rewind: true,
    arrows: false,
    height: '500px',
    speed: 750,
    breakpoints: {
      1024: {
        destroy: true,
      },
    }
  },
  thumbnails: {
    direction: 'ttb',
    rewind: true,
    pagination: false,
    fixedWidth: '300px',
    gap: 10,
    height: '150px',
    isNavigation: true,
    arrows: false,
    drag: false,
  }
}

// These constructs allow us to address object values using dynamic, or array-style selectors.
// object[key] instead of object.key.
// The keyof typeof informs TypeScript that any variable using one of these will only ever be a valid value as defined in the object.
type ContentType = keyof typeof options.selectors;

const accordions = {
  contentList: <any>[],
  thumbsList: <any>[],
  init: () => {
    const content_list: NodeList = accordions.gather('content');
    const thumbs_list: NodeList = accordions.gather('thumbs');
    const length: number = content_list.length;
    if (!length) return false;

    if (thumbs_list.length !== content_list.length) {
      // there's a mismatch in the number of thumbnails to the number of content accordions on the page.
      throw new Error('There is an unmatched number of thumbnail accordions and Content accordions on this page.');
    }

    // We have content, and matching thumbnail sliders, let's proceed!
    content_list.forEach((el: Node, idx: number) => {
      accordions.build.content(el as HTMLElement, idx);
      accordions.build.thumbs(thumbs_list[idx] as HTMLElement, idx);
    });

    accordions.contentList.forEach((accordion: Splide, idx: number) => {
      accordion.sync(accordions.thumbsList[idx]);
      accordion.mount();
      accordions.thumbsList[idx].mount();
    });
  },
  gather: (type: string) => document.querySelectorAll(options.selectors[type as ContentType]),
  observe: () => new MutationObserver((mutations) => {
    mutations.forEach((mutation: MutationRecord) => {
      const target = mutation.target as HTMLElement;
      if (target.classList.contains('is-active')) {
        accordions.transition(target);
      }
    });
  }),
  decorate: (el: HTMLElement, title: HTMLElement, bar: HTMLElement, evt: TransitionEvent) => {
    requestAnimationFrame(() => {
      const parentOffsetTop = Number(el.offsetTop);

      bar!.style.top = `${parentOffsetTop}px`;
      bar!.style.bottom = 'unset';
      bar!.style.height = `${title!.offsetHeight}px`;
      bar!.classList.remove('is-transitioning');

      if (el.closest('.sb-accordion')?.classList.contains('is-pristine')) return;
      const mql = window.matchMedia("(max-width: 1024px)");
      if (mql.matches) {
        const dims = el!.getBoundingClientRect();
        const headerDims: HTMLElement | null | undefined = document.querySelector<HTMLElement>('.sb-c-main-nav');
        const margin = headerDims!.offsetHeight;

        window.scrollTo({
          left: window.scrollX,
          top: (dims.top - margin) + window.scrollY,
          behavior: 'smooth',
        });
      }
    });
  },
  transition: (el: HTMLElement) => {
    const bar: HTMLElement | null | undefined = el.closest<HTMLElement>('.sb-accordion')?.querySelector<HTMLElement>(options.selectors.progressBar);
    if (!bar) {
      return false;
    }

    const title: HTMLElement | null | undefined  = el.querySelector<HTMLElement>(options.selectors.thumbTrigger);
    if (bar!.classList.contains('sb-carousel-direction-backward') ){
      if (!bar!.classList.contains('is-transitioning')) {
        bar!.style.height = `${Number(bar!.style.height.replace('px', '')) * 2}px`;
        bar!.style.top = `${title!.closest<HTMLElement>('li')!.offsetTop}px`;
        bar!.classList.add('is-transitioning');
      }
    } else {
      bar!.style.height = `50px`;
    }

    title!.addEventListener('transitionend', (evt: TransitionEvent) => {
      if (el.closest('li')?.classList.contains('is-active')) {
        accordions.decorate(el, title!, bar, evt)
      }
    });
  },
  build: {
    /**
     *
     * @param {HTMLElement} element - An HTML Element to be transformed into a Carousel.
     * @param {number} idx - The index of the accordion content in a possible list of accordion content items.
     */
    content: (element: HTMLElement , idx: number) => {
      accordions.contentList[idx] = new Splide(
        element,
        options.content
      );

    },
    /**
     *
     * @param {HTMLElement} element - An HTML Element to be transformed into a Carousel.
     * @param {number} idx - The index of the accordion thumbnails in a possible list of accordion thumbnail items.
     */
    thumbs: (element: HTMLElement , idx: number) => {
      const thumbSlider = new Splide(
        element,
        options.thumbnails
      );

      accordions.thumbsList[idx] = thumbSlider;

      const bar: HTMLElement | null | undefined = thumbSlider.root.querySelector<HTMLElement>(options.selectors.progressBar);

      thumbSlider.on('mounted', () => {
        thumbSlider.root.classList.add('is-pristine');
        const mut = accordions.observe();
        thumbSlider.Components.Slides.forEach((element) => {
          mut.observe(element.slide, {
            attributes: true,
            attributeFilter: ["class"]
          });
        });
      });

      thumbSlider.on('click', (slide) => {
        thumbSlider.root.classList.remove('is-pristine');
      });

      thumbSlider.on('inactive', (slide) => {
        thumbSlider.Components.Slides.forEach(element => {
          element.slide.classList.remove('was-active');
        });
        slide.slide.classList.add('was-active');
      })

      thumbSlider.on('move', (newIndex: number, prevIndex: number, destIndex: number) => {
        // Determine progress bar direction
        const classStub: string = 'sb-carousel-direction-';
        const directionClasses: string[] = ['forward', 'backward'];

        // clear out existing direction classes.
        directionClasses.forEach( (className: string) => {
          bar!.classList.remove(`${classStub}${className}`);
        })

        // make sure that we have values for the navigation targets.
        // this is not true when the slider is first mounted, as no navigation has occurred.
        if (String(newIndex) && String(prevIndex)) {
          bar!.classList.add(`${classStub}${directionClasses[Number(newIndex < prevIndex)]}`);
        }
      });
    },
  }
}

export default accordions;
